import type {MyDocument} from '@appManagers/appDocsManager';
import type LottiePlayer from '@lib/lottie/lottiePlayer';
import {applyColorOnContext} from '@lib/lottie/lottiePlayer';
import rootScope from '@lib/rootScope';
import getStickerThumbKey from '@lib/storages/utils/thumbs/getStickerThumbKey';
import customProperties from '@helpers/dom/customProperties';
import {bindManagersToSession} from '@lib/accounts/bindManagersToAccount';

const savingLottiePreview: {[docId: DocId]: {width: number, height: number}} = {};

export function isSavingLottiePreview(doc: MyDocument, toneIndex: number | string, width: number, height: number) {
  const key = getStickerThumbKey(doc.id, toneIndex);
  const saving = savingLottiePreview[key];
  return saving && saving.width >= width && saving.height >= height;
}

let sharedCanvas: HTMLCanvasElement, sharedContext: CanvasRenderingContext2D;
const createCanvas = () => {
  rootScope.addEventListener('theme_changed', () => {
    rootScope.managers.thumbsStorage.clearColoredStickerThumbs();

    for(const key in savingLottiePreview) {
      const [, toneIndex] = key.split('-');
      if(toneIndex && isNaN(+toneIndex)) {
        delete savingLottiePreview[key];
      }
    }
  });

  sharedCanvas = document.createElement('canvas');
  sharedContext = sharedCanvas.getContext('2d');
};

export async function saveLottiePreview(doc: MyDocument, canvas: HTMLCanvasElement | ImageBitmap, toneIndex: number | string) {
  const managers = bindManagersToSession(rootScope.managers);
  const key = getStickerThumbKey(doc.id, toneIndex);
  const {width, height} = canvas;
  if(isSavingLottiePreview(doc, toneIndex, width, height)) {
    return;
  }

  const saving = savingLottiePreview[key] = {
    width,
    height
  };

  const thumb = await managers.thumbsStorage.getStickerCachedThumb(doc.id, toneIndex);
  if(savingLottiePreview[key] !== saving) {
    return;
  }

  if(thumb && thumb.w >= width && thumb.h >= height) {
    return;
  }

  if(typeof(toneIndex) === 'string' || !(canvas instanceof HTMLCanvasElement)) {
    if(!sharedCanvas) {
      createCanvas();
    }

    sharedCanvas.width = width;
    sharedCanvas.height = height;
    sharedContext.drawImage(canvas, 0, 0, width, height);
    if(typeof(toneIndex) === 'string') {
      applyColorOnContext(sharedContext, customProperties.getProperty(toneIndex), 0, 0, width, height);
    }
    canvas = sharedCanvas;
  }

  const promise = new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob));
  });

  const blob = await promise;
  if(savingLottiePreview[key] !== saving) {
    return;
  }

  if(!blob) {
    console.error('trying to save sticker preview with no blob', arguments);
    debugger;
    return;
  }

  managers.thumbsStorage.saveStickerPreview(doc.id, blob, width, height, toneIndex);
}

export async function saveLottiePreviewFromPlayer(doc: MyDocument, player: LottiePlayer, toneIndex: number | string) {
  if(!player.offscreen) {
    return saveLottiePreview(doc, player.canvas[0], toneIndex);
  }

  if(isSavingLottiePreview(doc, toneIndex, player.width, player.height)) {
    return; // guard runs BEFORE the export - no bitmap ships when nothing needs saving
  }

  try {
    const {frame} = await player.exportFrame();
    await saveLottiePreview(doc, frame, toneIndex);
    frame.close?.();
  } catch(err) {
    // degrade to no persisted thumb - same as today's pre-firstFrame state
    console.error('saveLottiePreviewFromPlayer: exportFrame failed', err, player);
  }
}
